import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Target, Plus, Trash2, Save, Sparkles, Star } from "lucide-react";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { API } from "../App";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

const CapabilitiesExplorationPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [capabilities, setCapabilities] = useState([{ capability: "", level: "competent", evidence: "", goal: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);

  const AREAS = [
    { id: "professional", name: t("capabilities.professional"), examples: t("capabilities.professional.ex").split(",") },
    { id: "social", name: t("capabilities.social"), examples: t("capabilities.social.ex").split(",") },
    { id: "personal", name: t("capabilities.personal"), examples: t("capabilities.personal.ex").split(",") },
    { id: "creative", name: t("capabilities.creative"), examples: t("capabilities.creative.ex").split(",") },
    { id: "physicalSkills", name: t("capabilities.physicalSkills"), examples: t("capabilities.physicalSkills.ex").split(",") },
  ];

  const LEVELS = [
    { id: "learning", label: t("capabilities.learning") },
    { id: "competent", label: t("capabilities.competent") },
    { id: "proficient", label: t("capabilities.proficient") },
    { id: "expert", label: t("capabilities.expert") },
  ];

  const addCapability = (suggestion = "") => {
    setCapabilities(prev => [...prev, { capability: suggestion, level: "competent", evidence: "", goal: "" }]);
  };

  const removeCapability = (idx) => {
    if (capabilities.length <= 1) return;
    setCapabilities(prev => prev.filter((_, i) => i !== idx));
  };

  const updateCapability = (idx, field, value) => {
    setCapabilities(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  const handleSubmit = async () => {
    if (!capabilities.some(c => c.capability.trim())) {
      toast.error(t("capabilities.addAtLeastOne"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/capabilities-exploration`, {
        capabilities: capabilities.filter(c => c.capability.trim()),
      }, { withCredentials: true });
      toast.success(t("capabilities.saved"));
      if (user?.role === "patient") promptShare(res.data, "capabilities_exploration");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="capabilities-exploration-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("capabilities.title")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("capabilities.pageSubtitle")}</p>
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.capabilities_exploration} />

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Suggestions */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                {t("capabilities.competenceAreas")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {AREAS.map((area) => (
                <div key={area.id}>
                  <button
                    onClick={() => setSelectedArea(selectedArea === area.id ? null : area.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all text-sm font-medium ${
                      selectedArea === area.id ? "bg-primary/10 text-primary" : "hover:bg-muted"
                    }`}
                    data-testid={`area-${area.id}`}
                  >
                    {area.name}
                  </button>
                  {selectedArea === area.id && (
                    <div className="flex flex-wrap gap-1.5 mt-2 ml-3">
                      {area.examples.map((ex) => (
                        <button
                          key={ex}
                          onClick={() => addCapability(ex)}
                          className="text-xs px-2 py-1 rounded-full bg-secondary hover:bg-primary/10 transition-colors"
                        >
                          + {ex}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* My Capabilities */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  {t("capabilities.myCapabilities")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {capabilities.map((c, idx) => (
                  <div key={idx} className="p-4 border rounded-lg space-y-3">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1">
                        <Input
                          placeholder={t("capabilities.capabilityPlaceholder")}
                          value={c.capability}
                          onChange={(e) => updateCapability(idx, "capability", e.target.value)}
                          data-testid={`capability-${idx}`}
                        />
                      </div>
                      {capabilities.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeCapability(idx)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">{t("capabilities.currentLevel")}</label>
                      <div className="flex gap-2">
                        {LEVELS.map((level) => (
                          <button
                            key={level.id}
                            onClick={() => updateCapability(idx, "level", level.id)}
                            className={`px-3 py-1 rounded-full text-xs transition-all border ${
                              c.level === level.id ? "bg-primary text-white border-primary" : "bg-background border-border hover:bg-muted"
                            }`}
                          >
                            {level.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Input
                      placeholder={t("capabilities.evidencePlaceholder")}
                      value={c.evidence}
                      onChange={(e) => updateCapability(idx, "evidence", e.target.value)}
                    />
                    <Input
                      placeholder={t("capabilities.goalPlaceholder")}
                      value={c.goal}
                      onChange={(e) => updateCapability(idx, "goal", e.target.value)}
                    />
                  </div>
                ))}

                <Button variant="outline" onClick={() => addCapability()} className="w-full" data-testid="add-capability-btn">
                  <Plus className="w-4 h-4 mr-1" /> {t("capabilities.addCapability")}
                </Button>

                <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-primary hover:bg-primary/90" data-testid="save-capabilities">
                  <Save className="w-4 h-4 mr-1" /> {submitting ? t("exercise.saving") : t("capabilities.saveCapabilities")}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default CapabilitiesExplorationPage;
